import { get as getConfig } from '../../config/index'
import {
  DE,
  EN,
  ES,
  FR,
} from '../languages/constants'
import { getLanguageLocale } from '../languages/index'

export interface HreflangAlternate {
  href: string
  hreflang: string
  rel: 'alternate'
}

export interface BuildHreflangAlternatesOptions {
  /** Absolute or site-relative canonical URL for the current page. */
  canonicalHref: string
  /** Preserve indexable query keys such as fullplayer on alternates. */
  allowedQuery?: Record<string, string | undefined>
}

const SITE_LANGUAGES = [EN, ES, FR, DE] as const

/**
 * Language alternates for DocumentHead.
 *
 * Catalog paths are shared across locales (language is cookie / Accept-Language,
 * not a path prefix). Alternates therefore reuse the canonical path and select
 * locale via the `language` query param so crawlers can address each variant.
 * `x-default` points at the English variant.
 */
export function buildHreflangAlternates (
  options: BuildHreflangAlternatesOptions,
): HreflangAlternate[] {
  const { canonicalHref, allowedQuery = {} } = options
  const origin = String(getConfig().origin || 'https://www.gaia.com').replace(/\/$/, '')
  const absoluteBase = toAbsoluteUrl(canonicalHref, origin)
  const url = new URL(absoluteBase)

  Object.entries(allowedQuery).forEach(([key, value]) => {
    if (value) {
      url.searchParams.set(key, value)
    }
  })

  const alternates: HreflangAlternate[] = SITE_LANGUAGES.map((language) => {
    const hrefUrl = new URL(url.toString())
    hrefUrl.searchParams.set('language', language)
    return {
      rel: 'alternate',
      hreflang: getLanguageLocale(language),
      href: hrefUrl.toString(),
    }
  })

  const defaultUrl = new URL(url.toString())
  defaultUrl.searchParams.set('language', EN)
  alternates.push({
    rel: 'alternate',
    hreflang: 'x-default',
    href: defaultUrl.toString(),
  })

  return alternates
}

function toAbsoluteUrl (href: string, origin: string): string {
  if (/^https?:\/\//i.test(href)) {
    return href
  }
  const path = href.startsWith('/') ? href : `/${href}`
  return `${origin}${path}`
}
